import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchPendingProjects, acceptProject, rejectProject } from '../../api/api';

// Async Thunks

export const fetchPendingProjectsAsync = createAsyncThunk(
  'pendingProjects/fetchPendingProjects',
  async (params, { rejectWithValue }) => {
    try {
      // API returns an object with { data, pagination, message }
      const response = await fetchPendingProjects(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch pending projects');
    }
  }
);

export const acceptProjectAsync = createAsyncThunk(
  'pendingProjects/acceptProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await acceptProject(projectId);
      return response; // Expecting response.data contains the accepted project's info
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to accept project');
    }
  }
);

export const rejectProjectAsync = createAsyncThunk(
  'pendingProjects/rejectProject',
  async (projectId, { rejectWithValue }) => {
    try {
      const response = await rejectProject(projectId);
      return response; // Expecting response.data contains the rejected project's info
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to reject project');
    }
  }
);

// Slice

const pendingProjectsSlice = createSlice({
  name: 'pendingProjects',
  initialState: {
    projects: [],
    loading: false,
    error: null,
    // Initial pagination; will be replaced with backend data
    pagination: {
      current_page: 1,
      per_page: 10,
      total: 0,
      last_page: 1,
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Projects
      .addCase(fetchPendingProjectsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingProjectsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPendingProjectsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Accept Project
      .addCase(acceptProjectAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptProjectAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the accepted project from the list
        state.projects = state.projects.filter(
          (p) => p.id !== action.payload.data.id
        );
      })
      .addCase(acceptProjectAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Reject Project
      .addCase(rejectProjectAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectProjectAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the rejected project from the list
        state.projects = state.projects.filter(
          (p) => p.id !== action.payload.data.id
        );
      })
      .addCase(rejectProjectAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default pendingProjectsSlice.reducer;
